import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import type { Database } from "@/shared/types/database.types";

type LawyerRow = Database["public"]["Tables"]["lawyers"]["Row"];
type LawyerUpdate = Database["public"]["Tables"]["lawyers"]["Update"];
type LicensedStateRow = Database["public"]["Tables"]["lawyer_licensed_states"]["Row"];

export interface LicensedState {
  id?: number;
  state: string;
  bar_number: string;
}

export interface LawyerSettingsData {
  lawyer: LawyerRow | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useLawyerSettings(): LawyerSettingsData {
  const { user } = useAppContext();

  const {
    data: lawyer,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["lawyer-settings", user?.user_id],
    queryFn: async () => {
      if (!user?.user_id) return null;

      const { data, error: queryError } = await supabase
        .from("lawyers")
        .select("*")
        .eq("user_id", user.user_id)
        .maybeSingle();

      if (queryError) throw queryError;
      return data;
    },
    enabled: !!user?.user_id,
    staleTime: 2 * 60 * 1000,
  });

  return {
    lawyer: lawyer ?? null,
    isLoading,
    error: error as Error | null,
    refetch,
  };
}

export function useUpdateLawyerSettings() {
  const { user } = useAppContext();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (updates: LawyerUpdate) => {
      if (!user?.user_id) throw new Error("No user found");

      const { data, error } = await supabase
        .from("lawyers")
        .update(updates)
        .eq("user_id", user.user_id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lawyer-settings", user?.user_id] });
    },
  });

  return {
    updateLawyer: mutation.mutateAsync,
    isUpdating: mutation.isPending,
    error: mutation.error,
  };
}

export function useLawyerLicensedStates(lawyerId: number | undefined) {
  return useQuery({
    queryKey: ["lawyer-licensed-states", lawyerId],
    queryFn: async () => {
      if (!lawyerId) return [];

      const { data, error } = await supabase
        .from("lawyer_licensed_states")
        .select("*")
        .eq("lawyer_id", lawyerId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return (data ?? []) as LicensedStateRow[];
    },
    enabled: !!lawyerId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSaveLicensedStates() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ lawyerId, states }: { lawyerId: number; states: LicensedState[] }) => {
      const payload = states
        .filter((s) => s.state)
        .map((s) => ({ state: s.state, bar_number: s.bar_number || "" }));

      const { data, error } = await supabase.rpc("replace_lawyer_licensed_states", {
        p_lawyer_id: lawyerId,
        p_states:
          payload as unknown as Database["public"]["Functions"]["replace_lawyer_licensed_states"]["Args"]["p_states"],
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["lawyer-licensed-states", variables.lawyerId],
      });
    },
  });

  return {
    saveLicensedStates: mutation.mutateAsync,
    isSaving: mutation.isPending,
    error: mutation.error,
  };
}

export function useLawyerPhotoUrl(profilePhotoKey: string | null | undefined) {
  return useQuery({
    queryKey: ["lawyer-photo-url", profilePhotoKey],
    queryFn: async () => {
      if (!profilePhotoKey) return null;

      const {
        data: { session },
      } = await supabase.auth.getSession();
      const accessToken = session?.access_token;
      if (!accessToken) return null;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/media/profile-picture/url?lawyer_photo_key=${encodeURIComponent(profilePhotoKey)}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          },
        },
      );

      if (!response.ok) return null;

      const responseData = await response.json();
      return (responseData.photoUrl as string) ?? null;
    },
    enabled: !!profilePhotoKey,
    staleTime: 90 * 1000,
  });
}

export async function uploadLawyerPhoto(file: File, folder: string): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) return null;

  const presignedResponse = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/media/profile-picture/presigned-url`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ fileName: file.name, folder }),
    },
  );

  if (!presignedResponse.ok) return null;

  const { uploadUrl, fileKey } = await presignedResponse.json();

  const uploadResponse = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type },
  });

  if (!uploadResponse.ok) return null;

  return fileKey as string;
}
